import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  UncontrolledTooltip,
} from "reactstrap";
import { Link } from "react-router-dom";
import Breadcrumbs from "components/Common/Breadcrumb";
import CustomDatePicker from "common/CustomDatePicker";
import CustomSelect from "components/Common/custom-select";
import TableContainer from "components/Common/TableContainer";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { changeExpensesEntryStatus, fetchEmployeeDropdownAutoList, fetchExpenseEntry, fetchExpensesEntryMappedHeads, fetchExpensesHeads, fetchReimbursementMappedheads, saveExpenseEntry } from "store/actions";
import Swal from "sweetalert2";
import { convertToInputDate } from "common/dateFormat/formatDate";
import Loader from "common/pageLoader";

const ExpenseTransactionEntry = () => {
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    dropdownAutoList,
    masterheads,
    mappedheads,
    expenseEntryList,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    dropdownAutoList: state.EmployeeDetailsReducer?.dropdownAutoList ?? [],
    masterheads: state.ExpensesMasterReducer?.masterheads ?? [],
    mappedheads: state.ExpensesMasterReducer?.mappedheads ?? [],
    expenseEntryList: state.ExpensesMasterReducer?.expenseEntryList ?? [],

    loading: state.ExpensesMasterReducer?.loading || state.EmployeeDetailsReducer?.loading,
    error: state.ExpensesMasterReducer?.error || state.EmployeeDetailsReducer?.error,
    success: state.ExpensesMasterReducer?.success,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchEmployeeDropdownAutoList(unitId));
    dispatch(fetchExpenseEntry(unitId))
    dispatch(fetchExpensesHeads(unitId));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      dispatch(fetchExpenseEntry(authDetails?.unit_id));
      formik.resetForm();
      setIsEdit(false);
      setEditId(null);
      setExistingFileName(null);
      setExistingFilePath(null);
      const fileInput = document.getElementById("attachment");
      if (fileInput) fileInput.value = "";
    }
  }, [authDetails, success, dispatch]);

  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [existingFileName, setExistingFileName] = useState(null);
  const [existingFilePath, setExistingFilePath] = useState(null);

  // const handleReset = () => {
  //   formik.handleReset();        // Resets form fields
  //   setIsEdit(false);            // Exit edit mode
  //   setEditId(null);             // Clear the edited ID
  // };
  const handleReset = () => {
    // check if form is dirty or in edit mode
    if (formik.dirty || isEdit) {
      Swal.fire({
        title: "Are you sure?",
        text: "Unsaved changes will be lost.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        reverseButtons: false,
      }).then((result) => {
        if (result.isConfirmed) {
          formik.resetForm();
          setIsEdit(false);
          setEditId(null);
          setExistingFileName(null);
          setExistingFilePath(null);
          const fileInput = document.getElementById("attachment");
          if (fileInput) fileInput.value = "";
        }
      });
    } else {
      // no changes, reset directly
      formik.resetForm();
      setIsEdit(false);
      setEditId(null);
    }
  };

  const formik = useFormik({
    validateOnBlur:true,
    validateOnChange:false,
    initialValues: {
      employee: null,
      empNo: "",
      expDate: null,
      head: null,
      subHead: null,
      amount: "",
      remarks: "",
      attachment: null,
    },
    validationSchema: Yup.object({
      employee: Yup.object().nullable().required("Employee is required"),
      expDate: Yup.date().nullable().required("Expense Date is required"),
      head: Yup.object().nullable().required("Head is required"),
      subHead: Yup.object().nullable().required("Sub Head is required"),
      amount: Yup.number().typeError("Amount must be a number").positive("Amount must be greater than 0").required("Amount is required").max(999999, "Amount cannot exceed 6 digits"),
      remarks: Yup.string().trim().max(500, "Remarks cannot exceed 500 characters").required("Remarks are required"),
      attachment: Yup.mixed().nullable().test("fileType", "Only JPG, PNG, or PDF files are allowed",
        (value) => {
          if (!value) return true;
          return ["image/jpeg", "image/png", "application/pdf"].includes(value.type);
        }
      ),
    }),
    onSubmit: async (values, { resetForm }) => {
      const formData = new FormData();
      formData.append("expense_id", editId ? editId : 0);
      formData.append("expenseshead_id", values.head?.value?.toString() || "");
      formData.append("expensessubhead_id", values.subHead?.value?.split('_')[0] || "");
      formData.append("emp_id", values.employee?.value?.toString() || "");
      formData.append("expense_date", values.expDate || "");
      formData.append("expense_amount", values.amount?.toString() || "");
      formData.append("remark", values.remarks || "");
      formData.append("unit_id", authDetails?.unit_id);
      formData.append("create_by", authDetails?.user_id);
      if (values.attachment) {
        formData.append("file_attach", values.attachment);
      } else if (isEdit && existingFilePath) {
        try {
          const baseUrl = process.env.REACT_APP_IMAGE_BASE_URL || "";
          const response = await fetch(`${baseUrl}${existingFilePath}`);
          const blob = await response.blob();
          const fileName = existingFilePath.split('/').pop();
          formData.append("file_attach", new File([blob], fileName, { type: blob.type }));
        } catch (e) {
          // if fetch fails, skip file
        }
      }
      dispatch(saveExpenseEntry(formData));
    },
  });

  const handleToggleStatus = (id, isActive) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to ${isActive ? 'deactivate' : 'activate'} this expense entry.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      dangerMode: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("exp_claim_id", id);
        formData.append("create_by", authDetails?.user_id);
        formData.append("unit_id", authDetails?.unit_id);
        dispatch(changeExpensesEntryStatus(formData));
      }
    });
  };

  const handleEdit = (row) => {
    setIsEdit(true);
    setEditId(row.exp_claim_id);

    dispatch(fetchExpensesEntryMappedHeads(authDetails?.unit_id, row.head_id));

    const employeeOption = { value: row.emp_id.toString(), label: row.emp_name };
    const headOption = { value: row.head_id.toString(), label: row.head_name };
    const subHeadOption = { value: `${row.subhead_id}_0`, label: row.sub_head };

    setExistingFileName(row.file_path ? row.file_path.split('/').pop() : null);
    setExistingFilePath(row.file_path || null);
    formik.setValues({
      employee: employeeOption,
      empNo: row.emp_no,
      expDate: convertToInputDate(row.exp_date),
      head: headOption,
      subHead: subHeadOption,
      amount: row.exp_amount,
      remarks: row.remarks,
      attachment: null,
    });
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?", text: "This expense entry will be deleted.", icon: "warning", showCancelButton: true, confirmButtonColor: "#d33", confirmButtonText: "Yes, delete", cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("exp_claim_id", id);
        formData.append("create_by", authDetails?.user_id);
        formData.append("unit_id", authDetails?.unit_id);

        // ✅ reuse existing action
        dispatch(changeExpensesEntryStatus(formData));
      }
    });
  };
 

  const columns = [
    {
      Header: "S.No",
      Cell: ({ row }) => expenseEntryList.length - row.index,
      className: "text-center",
    },
    {
      Header: "Action",
      className: "action-col",
      Cell: ({ row }) => {
        const { exp_claim_id, approval_status } = row.original;

        return (
          <div className="d-flex gap-3 justify-content-center">
            {/* Edit */}
            {approval_status !== 1 && (
              <Link
                to="#"
                className="text-success"
                onClick={() => handleEdit(row.original)}
              >
                <i className="bx bxs-edit font-size-18" />
              </Link>
            )}

            {/* Delete */}
            {approval_status !== 1 && (
            <Link
              to="#"
              className="text-danger"
              onClick={() => handleDelete(exp_claim_id)}
            >
              <i
                className="mdi mdi-delete font-size-18"
                id={`delete-tooltip-${exp_claim_id}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`delete-tooltip-${exp_claim_id}`}
              >
                Delete
              </UncontrolledTooltip>
            </Link>
            )}

            {/* View File */}
            {row.original.file_path && (
              <Link
                to="#"
                className="text-info"
                onClick={(e) => {
                  e.preventDefault();
                  const baseUrl = process.env.REACT_APP_IMAGE_BASE_URL || "";
                  window.open(`${baseUrl}${row.original.file_path}`, "_blank");
                }}
              >
                <i className="mdi mdi-eye font-size-18" />
              </Link>
            )}
          </div>
        );
      },
    },
    {
      Header: "Employee",
      accessor: "emp_name",
    },
    {
      Header: "Expense Date",
      accessor: "exp_date",
    },
    {
      Header: "Head",
      accessor: "head_name",
    },
    {
      Header: "Sub Head",
      accessor: "sub_head",
    },
    {
      Header: "Amount",
      accessor: "exp_amount",
    },
    {
      Header: "Remarks",
      accessor: "remarks",
    },
    {
      Header: "Status",
      Cell: ({ row }) => {
        const status = row.original.a_status;
        return (
          <span className={`badge ${status === "Approved" ? "bg-success" : status === "Rejected" ? "bg-danger" : "bg-warning"}`}>
            {status || "Pending"}
          </span>
        );
      },
    },

  ];


  return (
    <div className="page-content">
      <Loader loading={loading} />
      <Container fluid>
        <Breadcrumbs title="Expenses" breadcrumbItem="Expense Entry" />

        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit}>
              <Row className="mb-3">
                <Col md={3}>
                  <FormGroup>
                    <Label for="empNo">Employee <span className="text-danger">*</span></Label>
                    <CustomSelect
                      id="employee"
                      name="employee"
                      options={(dropdownAutoList || []).map(bg => ({ value: bg.emp_id.toString(), label: bg.emp_no }))}
                      value={formik.values.employee || null}
                      onChange={(selectedOption) => formik.setFieldValue("employee", selectedOption)}
                      onBlur={() => formik.setFieldTouched("employee", true)}
                      className={formik.touched.employee && formik.errors.employee ? "is-invalid" : ""}
                    />
                    {formik.touched.employee && formik.errors.employee ? <div className="text-danger">{formik.errors.employee}</div> : null}
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label for="expDate">Expense Date <span className="text-danger">*</span></Label>
                    <CustomDatePicker
                      value={formik.values.expDate}
                      onChange={(date) => formik.setFieldValue("expDate", date)}
                      onBlur={() => formik.setFieldTouched("expDate", true)}
                    />
                    {formik.touched.expDate && formik.errors.expDate && (
                      <div className="text-danger">{formik.errors.expDate}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label for="head">Head <span className="text-danger">*</span></Label>
                    <CustomSelect
                      id="head"
                      name="head"
                      options={(masterheads || []).map(emp => ({ value: emp.exp_id.toString(), label: emp.expense_name }))}
                      value={formik.values.head || null}
                      onChange={(selectedOption) => {
                        formik.setFieldValue("head", selectedOption);
                        formik.setFieldValue("subHead", null);
                        dispatch(fetchExpensesEntryMappedHeads(authDetails?.unit_id, selectedOption?.value));
                      }}
                    />
                    {formik.touched.head && formik.errors.head && (
                      <div className="text-danger">{formik.errors.head}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label for="subHead">Sub Head <span className="text-danger">*</span></Label>
                    <CustomSelect
                      id="subHead"
                      name="subHead"
                      options={(mappedheads || []).map((emp, index) => ({ value: `${emp.subhead_id}_${index}`, label: emp.subheads_name }))}
                      value={formik.values.subHead || null}
                      onChange={(selectedOption) => formik.setFieldValue("subHead", selectedOption)}
                    />
                    {formik.touched.subHead && formik.errors.subHead && (
                      <div className="text-danger">{formik.errors.subHead}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label for="amount">Amount <span className="text-danger">*</span></Label>
                    {/* <Input
                      id="amount"
                      name="amount"
                      type="number"
                      value={formik.values.amount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.amount && Boolean(formik.errors.amount)}
                    /> */}
                    <Input
                      id="amount"
                      name="amount"
                      type="text"
                      inputMode="decimal"     // mobile numeric keypad
                      placeholder="Enter amount"
                      value={formik.values.amount}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          formik.setFieldValue("amount", value);
                        }
                      }}
                      onKeyDown={(e) => {
                        // block e, E, +, -, etc
                        if (["e", "E", "+", "-"].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onBlur={formik.handleBlur}
                      invalid={(formik.touched.amount && Boolean(formik.errors.amount)) || Number(formik.values.amount) > 999999}
                    />
                    {Number(formik.values.amount) > 999999 ? (
                      <div className="text-danger">Amount cannot exceed 6 digits</div>
                    ) : formik.touched.amount && formik.errors.amount ? (
                      <div className="text-danger">{formik.errors.amount}</div>
                    ) : null}
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label for="remarks">Remarks <span className="text-danger">*</span></Label>
                    <textarea
                      id="remarks"
                      name="remarks"
                      maxLength={500}
                      value={formik.values.remarks}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`form-control ${formik.touched.remarks && formik.errors.remarks ? "is-invalid" : ""}`}
                      style={{ height: "60px" }}
                    />
                    {formik.touched.remarks && formik.errors.remarks && (
                      <div className="text-danger">{formik.errors.remarks}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label for="attachment">Attachment</Label>
                    <div className="d-flex align-items-center form-control p-0" style={{ overflow: "hidden" }}>
                      <label htmlFor="attachment" className="mb-0 px-2 py-1" style={{ background: "#e9ecef", borderRight: "1px solid #ced4da", cursor: "pointer", whiteSpace: "nowrap", fontSize: "0.875rem" }}>Choose file</label>
                      <span className="px-2 text-truncate" style={{ fontSize: "0.875rem", color: formik.values.attachment || existingFileName ? "#495057" : "#6c757d" }}>
                        {formik.values.attachment ? formik.values.attachment.name : existingFileName || "No file chosen"}
                      </span>
                    </div>
                    <input
                      id="attachment"
                      name="attachment"
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      style={{ display: "none" }}
                      onChange={(event) =>
                        formik.setFieldValue("attachment", event.currentTarget.files[0] || null)
                      }
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.attachment && formik.errors.attachment && (
                      <div className="text-danger">{formik.errors.attachment}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col className="d-flex align-items-center justify-content-end mt-4">
                  <Button type="submit" className="btn button-save me-2">
                    <i className="fas fa-save me-1" /> {isEdit ? "Update" : "Save"}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleReset}
                    className="btn button-cancel"> <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <TableContainer
              columns={columns}
              data={expenseEntryList}
              isGlobalFilter={true}
              customPageSize={10}
              tableSorter={true}
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ExpenseTransactionEntry;
